import React, { useState } from "react";
import { X } from 'lucide-react';
import { toast } from "react-toastify";
import useApi from "@src/api_service/useApi";
import { ApiErrorBody, SchoolPaymentLink } from "../interface";
import { authHeaders, errorMessage, formatMoney } from "../utils";

// Resend and cancel are shared by the list and the detail page.
export const useLinkActions = (onChanged: () => void) => {
  const { loading, post } = useApi();

  const resend = async (link: SchoolPaymentLink) => {
    let ok = false;
    await post<{ message?: string }, ApiErrorBody>(
      `/third_party/school_payment_links/${link._id}/resend`,
      {} as unknown as FormData,
      authHeaders(),
      {
        onSuccess: (res) => {
          ok = true;
          toast.success(res?.message || `Payment link resent to ${link.recipientEmail}`);
          onChanged();
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Could not resend the payment link'));
          onChanged();
        },
      }
    );
    return ok;
  };

  const cancel = async (link: SchoolPaymentLink, reason: string) => {
    let ok = false;
    await post<{ message?: string }, ApiErrorBody>(
      `/third_party/school_payment_links/${link._id}/cancel`,
      { reason } as unknown as FormData,
      authHeaders(),
      {
        onSuccess: (res) => {
          ok = true;
          toast.success(res?.message || 'Payment link cancelled');
          onChanged();
        },
        onError: (err) => {
          toast.error(errorMessage(err, 'Could not cancel the payment link'));
          onChanged();
        },
      }
    );
    return ok;
  };

  return { loading, resend, cancel };
};

interface CancelLinkModalProps {
  link: SchoolPaymentLink | null;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

export const CancelLinkModal: React.FC<CancelLinkModalProps> = ({ link, loading, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');

  if (!link) return null;

  const close = () => {
    setReason('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Cancel payment link</h2>
          <button onClick={close} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          <span className="font-medium text-gray-800">{link.organizationName || link.thirdPartyAppId}</span> will no longer be able to
          pay {formatMoney(link.amount, link.currency)} through this link. A checkout the school already opened can still complete.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onConfirm(reason.trim());
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Reason (optional)</label>
            <textarea
              value={reason}
              maxLength={1000}
              rows={3}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Deal renegotiated, sending a new link"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#E55109] focus:border-transparent outline-none text-sm"
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={close}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Keep link
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 text-sm font-medium"
            >
              {loading ? 'Cancelling...' : 'Cancel link'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
